export const packageId = '@ai-factory/i18n' as const;

export const DEFAULT_LOCALE = 'en-US' as const;
export const DEFAULT_TIMEZONE = 'UTC' as const;
export const DEFAULT_CURRENCY = 'USD' as const;

export const supportedLocales = [
  'en-US',
  'en-GB',
  'en-AU',
  'de-DE',
  'es-ES',
  'fr-FR',
  'it-IT',
  'pt-BR',
  'ja-JP',
  'ko-KR',
  'zh-CN',
  'ar',
] as const;

export type SupportedLocale = (typeof supportedLocales)[number];
export type LocaleOption = { value: SupportedLocale; label: string };
// These are the locales whose public product copy is complete at launch. The
// broader `supportedLocales` set remains available to server-side AI, RAG and
// email flows, but must not be advertised by a UI that would otherwise render
// its English fallback in a different writing direction.
export const launchLocales = [
  'en-US',
  'en-GB',
  'en-AU',
  'es-ES',
  'zh-CN',
] as const satisfies readonly SupportedLocale[];
export type LaunchLocale = (typeof launchLocales)[number];
export type AppI18nNamespace =
  | 'platform'
  | 'home_visual'
  | 'product_visual'
  | 'sales_agent'
  | 'tarot'
  | 'auth'
  | 'billing'
  | 'email'
  | 'widget'
  | 'ai'
  | 'rag'
  | 'state';
export type TextDirection = 'ltr' | 'rtl';
export type TranslationVariables = Record<string, string | number | boolean | null | undefined>;
export type TranslationMessages = Record<string, string>;
export type TranslationCatalog = Partial<Record<SupportedLocale, TranslationMessages>>;

export interface LocaleContext {
  locale: SupportedLocale;
  timezone: string;
  currency: string;
  direction: TextDirection;
}

export interface LocalePreferenceInput {
  locale?: string | null;
  timezone?: string | null;
  currency?: string | null;
}

export interface TranslatorOptions {
  namespace: AppI18nNamespace;
  locale?: string | null;
  catalog: TranslationCatalog;
  fallbackCatalog?: TranslationMessages;
  onMissing?: (event: TranslationMissingEvent) => void;
}

export interface TranslationMissingEvent {
  namespace: AppI18nNamespace;
  locale: SupportedLocale;
  key: string;
}

export interface HreflangLink {
  hrefLang: string;
  href: string;
}

const rtlLanguageCodes = new Set(['ar', 'fa', 'he', 'ps', 'ur']);
const localeStorageKey = 'ai_factory.locale_context.v1';
const localeSelectionStorageKey = 'ai_factory.locale_selection.v1';
const localeSelectionLifetimeMs = 15 * 60 * 1_000;

export const localeOptions: readonly LocaleOption[] = [
  { value: 'en-US', label: 'English (US)' },
  { value: 'en-GB', label: 'English (UK)' },
  { value: 'en-AU', label: 'English (Australia)' },
  { value: 'de-DE', label: 'Deutsch' },
  { value: 'es-ES', label: 'Español' },
  { value: 'fr-FR', label: 'Français' },
  { value: 'it-IT', label: 'Italiano' },
  { value: 'pt-BR', label: 'Português (Brasil)' },
  { value: 'ja-JP', label: '日本語' },
  { value: 'ko-KR', label: '한국어' },
  { value: 'zh-CN', label: '简体中文' },
  { value: 'ar', label: 'العربية' },
];

export const launchLocaleOptions: readonly LocaleOption[] = localeOptions
  .filter((option): option is LocaleOption & { value: LaunchLocale } =>
    launchLocales.includes(option.value as LaunchLocale));

function canonicalLocale(value: string): string | null {
  try {
    return Intl.getCanonicalLocales(value)[0] ?? null;
  } catch {
    return null;
  }
}

export function resolveLocale(value?: string | null, fallback: SupportedLocale = DEFAULT_LOCALE): SupportedLocale {
  const canonical = typeof value === 'string' ? canonicalLocale(value.trim()) : null;
  if (!canonical) return fallback;

  const exact = supportedLocales.find((locale) => locale.toLowerCase() === canonical.toLowerCase());
  if (exact) return exact;

  const language = canonical.split('-')[0]?.toLowerCase();
  return supportedLocales.find((locale) => locale.split('-')[0]?.toLowerCase() === language) ?? fallback;
}

export function resolveLaunchLocale(
  value?: string | null,
  fallback: LaunchLocale = DEFAULT_LOCALE,
): LaunchLocale {
  const locale = resolveLocale(value, fallback);
  return launchLocales.includes(locale as LaunchLocale)
    ? locale as LaunchLocale
    : fallback;
}

export function resolveTimezone(value?: string | null, fallback = DEFAULT_TIMEZONE): string {
  if (typeof value !== 'string' || !value.trim()) return fallback;
  try {
    return new Intl.DateTimeFormat('en-US', { timeZone: value.trim() }).resolvedOptions().timeZone;
  } catch {
    return fallback;
  }
}

export function resolveCurrency(value?: string | null, fallback = DEFAULT_CURRENCY): string {
  const currency = typeof value === 'string' ? value.trim().toUpperCase() : '';
  if (!/^[A-Z]{3}$/.test(currency)) return fallback;
  try {
    new Intl.NumberFormat('en-US', { style: 'currency', currency });
    return currency;
  } catch {
    return fallback;
  }
}

export function textDirection(locale?: string | null): TextDirection {
  const language = (resolveLocale(locale).split('-')[0] ?? 'en').toLowerCase();
  return rtlLanguageCodes.has(language) ? 'rtl' : 'ltr';
}

export function createLocaleContext(input: LocalePreferenceInput = {}): LocaleContext {
  const locale = resolveLocale(input.locale);
  return {
    locale,
    timezone: resolveTimezone(input.timezone),
    currency: resolveCurrency(input.currency),
    direction: textDirection(locale),
  };
}

function storedLocalePreference(): LocalePreferenceInput {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(localeStorageKey);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const value = parsed as Record<string, unknown>;
    const preference: LocalePreferenceInput = {};
    if (typeof value.locale === 'string') preference.locale = value.locale;
    if (typeof value.timezone === 'string') preference.timezone = value.timezone;
    if (typeof value.currency === 'string') preference.currency = value.currency;
    return preference;
  } catch {
    return {};
  }
}

export function browserLocaleContext(input: LocalePreferenceInput = {}): LocaleContext {
  const browserLocale = typeof navigator === 'undefined' ? undefined : navigator.language;
  // Every public application ships with an explicit document language. Prefer
  // that launch-language contract over an unselected browser locale so an
  // English-first funnel does not render otherwise-English screens with, for
  // example, Chinese dates or currency punctuation. A visitor's URL choice or
  // saved choice still has higher precedence and remains fully localized.
  const documentLocale = typeof document === 'undefined'
    ? undefined
    : document.documentElement.lang || undefined;
  const queryLocale = typeof location === 'undefined'
    ? undefined
    : new URLSearchParams(location.search).get('locale') ?? undefined;
  const stored = storedLocalePreference();
  const browserTimezone = typeof Intl === 'undefined'
    ? undefined
    : Intl.DateTimeFormat().resolvedOptions().timeZone;
  const detected: LocalePreferenceInput = {};
  const locale = input.locale ?? queryLocale ?? stored.locale ?? documentLocale ?? browserLocale;
  const timezone = input.timezone ?? stored.timezone ?? browserTimezone;
  if (locale) detected.locale = locale;
  if (timezone) detected.timezone = timezone;
  const currency = input.currency ?? stored.currency;
  if (currency) detected.currency = currency;
  return createLocaleContext(detected);
}

export function launchBrowserLocaleContext(input: LocalePreferenceInput = {}): LocaleContext {
  const context = browserLocaleContext(input);
  return createLocaleContext({
    locale: resolveLaunchLocale(context.locale),
    timezone: context.timezone,
    currency: context.currency,
  });
}

export function applyDocumentLocale(context: Pick<LocaleContext, 'locale' | 'direction'>): void {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = context.locale;
  document.documentElement.dir = context.direction;
}

export function rememberLocaleContext(input: LocalePreferenceInput = {}): LocaleContext {
  const context = createLocaleContext(input);
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(
        localeStorageKey,
        JSON.stringify({
          locale: context.locale,
          timezone: context.timezone,
          currency: context.currency,
        }),
      );
    } catch {
      // Privacy mode or embedded environments may reject local storage. The
      // returned context still lets the current document render correctly.
    }
  }
  applyDocumentLocale(context);
  return context;
}

type BrowserLocaleSelection = {
  applicationCode: string;
  locale: string;
  timezone: string;
  currency: string;
  selectedAt: number;
};

function storedBrowserLocaleSelection(): BrowserLocaleSelection | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(localeSelectionStorageKey);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    const value = parsed as Record<string, unknown>;
    if (
      typeof value.applicationCode !== 'string' ||
      typeof value.locale !== 'string' ||
      typeof value.timezone !== 'string' ||
      typeof value.currency !== 'string' ||
      typeof value.selectedAt !== 'number'
    ) return null;
    return {
      applicationCode: value.applicationCode,
      locale: value.locale,
      timezone: value.timezone,
      currency: value.currency,
      selectedAt: value.selectedAt,
    };
  } catch {
    return null;
  }
}

/** Returns a recent, explicit choice that should be persisted for one application. */
export function pendingBrowserLocaleSelection(applicationCode: string): LocaleContext | null {
  const selection = storedBrowserLocaleSelection();
  if (
    !selection ||
    selection.applicationCode !== applicationCode ||
    Date.now() - selection.selectedAt > localeSelectionLifetimeMs
  ) return null;
  return createLocaleContext(selection);
}

export function clearBrowserLocaleSelection(applicationCode: string): void {
  if (typeof window === 'undefined') return;
  const selection = storedBrowserLocaleSelection();
  if (selection?.applicationCode !== applicationCode) return;
  try {
    window.localStorage.removeItem(localeSelectionStorageKey);
  } catch {
    // The preference is still harmlessly ignored after its short lifetime.
  }
}

/**
 * Persists a visitor-selected locale and reloads the current page with a
 * canonical locale query parameter. This keeps public links, SEO alternates,
 * and the application shell in the same language without requiring a router.
 */
export function selectBrowserLocale(locale: string, applicationCode?: string): LocaleContext {
  const current = browserLocaleContext();
  const context = rememberLocaleContext({
    locale,
    timezone: current.timezone,
    currency: current.currency,
  });

  if (typeof window !== 'undefined') {
    if (applicationCode) {
      try {
        window.localStorage.setItem(
          localeSelectionStorageKey,
          JSON.stringify({
            applicationCode,
            locale: context.locale,
            timezone: context.timezone,
            currency: context.currency,
            selectedAt: Date.now(),
          } satisfies BrowserLocaleSelection),
        );
      } catch {
        // The explicit URL and the general browser preference remain valid.
      }
    }
    const url = new URL(window.location.href);
    url.searchParams.set('locale', context.locale);
    window.location.assign(url.toString());
  }

  return context;
}

export function selectLaunchLocale(locale: string, applicationCode?: string): LocaleContext {
  return selectBrowserLocale(resolveLaunchLocale(locale), applicationCode);
}

export function formatCurrencyMinor(
  amount: number,
  currency: string,
  context: Pick<LocaleContext, 'locale'> | string = DEFAULT_LOCALE,
): string {
  const locale = typeof context === 'string' ? resolveLocale(context) : context.locale;
  const normalizedCurrency = resolveCurrency(currency);
  const fractionDigits = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: normalizedCurrency,
  }).resolvedOptions().maximumFractionDigits ?? 2;
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: normalizedCurrency,
  }).format(amount / 10 ** fractionDigits);
}

export function formatNumber(
  value: number,
  context: Pick<LocaleContext, 'locale'> | string = DEFAULT_LOCALE,
  options: Intl.NumberFormatOptions = {},
): string {
  const locale = typeof context === 'string' ? resolveLocale(context) : context.locale;
  return new Intl.NumberFormat(locale, options).format(value);
}

export function formatCurrency(
  amount: number,
  currency: string,
  context: Pick<LocaleContext, 'locale'> | string = DEFAULT_LOCALE,
  options: Intl.NumberFormatOptions = {},
): string {
  const locale = typeof context === 'string' ? resolveLocale(context) : context.locale;
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: resolveCurrency(currency),
    ...options,
  }).format(amount);
}

export function formatDateTime(
  value: Date | string | number,
  context: Pick<LocaleContext, 'locale' | 'timezone'>,
  options: Intl.DateTimeFormatOptions = { dateStyle: 'medium', timeStyle: 'short' },
): string {
  const date = value instanceof Date ? value : new Date(value);
  return new Intl.DateTimeFormat(context.locale, {
    ...options,
    timeZone: context.timezone,
  }).format(date);
}

export function interpolate(template: string, variables: TranslationVariables = {}): string {
  return template.replace(/\{\{\s*([\w.-]+)\s*\}\}/g, (source, name: string) => {
    const value = variables[name];
    return value === undefined || value === null ? source : String(value);
  });
}

export function createTranslator(options: TranslatorOptions) {
  const locale = resolveLocale(options.locale);
  const messages = options.catalog[locale] ?? {};
  const fallback = options.fallbackCatalog ?? options.catalog[DEFAULT_LOCALE] ?? {};
  return (key: string, variables?: TranslationVariables): string => {
    const template = messages[key] ?? fallback[key];
    if (template === undefined) {
      options.onMissing?.({ namespace: options.namespace, locale, key });
      return key;
    }
    return interpolate(template, variables);
  };
}

export function findMissingTranslations(
  catalog: TranslationCatalog,
  requiredKeys: readonly string[],
  locales: readonly SupportedLocale[] = supportedLocales,
): TranslationMissingEvent[] {
  return locales.flatMap((locale) => requiredKeys
    .filter((key) => catalog[locale]?.[key] === undefined)
    .map((key) => ({ namespace: 'platform' as const, locale, key })));
}

export function localeToHreflang(locale: string): string {
  return resolveLocale(locale);
}

export function createHreflangLinks(
  path: string,
  origin: string,
  locales: readonly SupportedLocale[] = supportedLocales,
  defaultLocale: SupportedLocale = DEFAULT_LOCALE,
): HreflangLink[] {
  const base = origin.replace(/\/$/, '');
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return [
    ...locales.map((locale) => ({
      hrefLang: localeToHreflang(locale),
      href: `${base}${normalizedPath}?locale=${encodeURIComponent(localeToHreflang(locale))}`,
    })),
    { hrefLang: 'x-default', href: `${base}${normalizedPath}?locale=${encodeURIComponent(localeToHreflang(defaultLocale))}` },
  ];
}

export function applyHreflangLinks(
  path: string,
  origin: string,
  locales: readonly SupportedLocale[] = supportedLocales,
): void {
  if (typeof document === 'undefined') return;
  document.querySelectorAll('link[data-ai-factory-hreflang="true"]').forEach((node) => node.remove());
  for (const alternate of createHreflangLinks(path, origin, locales)) {
    const link = document.createElement('link');
    link.rel = 'alternate';
    link.hreflang = alternate.hrefLang;
    link.href = alternate.href;
    link.dataset.aiFactoryHreflang = 'true';
    document.head.append(link);
  }
}

export function createAiLocaleContext(input: LocalePreferenceInput = {}) {
  const context = createLocaleContext(input);
  return { locale: context.locale, timezone: context.timezone, responseLanguage: context.locale };
}

export function createRagLocaleFilter(input: LocalePreferenceInput = {}) {
  const context = createLocaleContext(input);
  return { locale: context.locale, fallbackLocale: DEFAULT_LOCALE };
}

export function createEmailLocaleContext(input: LocalePreferenceInput = {}) {
  const context = createLocaleContext(input);
  return { locale: context.locale, timezone: context.timezone, direction: context.direction };
}

export const coreEnglishMessages: TranslationMessages = {
  'common.cancel': 'Cancel',
  'common.close': 'Close',
  'common.confirm': 'Confirm',
  'common.continue': 'Continue',
  'common.retry': 'Try again',
  'common.save': 'Save changes',
  'common.loading': 'Loading…',
  'billing.month': '/ month',
  'billing.year': '/ year',
  'billing.one_time': 'one time',
  'billing.sign_in_required': 'Please sign in before managing billing.',
  'billing.request_failed': 'Billing request failed.',
  'billing.nav_label': 'Billing navigation',
  'billing.nav_pricing': 'Pricing',
  'billing.nav_billing': 'Billing',
  'billing.account_required': 'Open pricing from your account, or add ?app_account_id=… to this URL.',
  'billing.checkout_failed': 'Checkout failed.',
  'billing.pricing_kicker': 'Simple, transparent billing',
  'billing.pricing_title': 'Choose the plan that fits your next chapter.',
  'billing.pricing_body': 'Plans and add-ons are isolated to {{brand}}. Paddle handles tax and secure payment.',
  'billing.loading_plans_title': 'Loading plans',
  'billing.loading_plans_body': 'Fetching the live product catalog…',
  'billing.no_plans_title': 'No public plans yet',
  'billing.no_plans_body': 'This application does not have an active Paddle catalog.',
  'billing.plans_label': '{{brand}} plans',
  'billing.product_subscription': 'subscription',
  'billing.product_usage_pack': 'credit pack',
  'billing.product_addon': 'add-on',
  'billing.product_base_plan': 'plan',
  'billing.feature_monthly_image_credits': '{{count}} image Credits each month',
  'billing.feature_monthly_visual_credits': '{{count}} Visual Credits each month',
  'billing.feature_monthly_assistant_messages': '{{count}} AI replies each month',
  'billing.feature_one_brand': '1 brand profile',
  'billing.feature_max_brands': 'Up to {{count}} brand profiles',
  'billing.feature_max_products': 'Up to {{count}} products',
  'billing.feature_one_member': '1 team member',
  'billing.feature_max_members': 'Up to {{count}} team members',
  'billing.feature_one_website': '1 website',
  'billing.feature_max_websites': 'Up to {{count}} websites',
  'billing.feature_one_agent': '1 Agent member',
  'billing.feature_max_agents': 'Up to {{count}} Agent members',
  'billing.feature_max_knowledge_documents': 'Up to {{count}} knowledge documents',
  'billing.feature_max_knowledge_storage': '{{count}} MB knowledge storage',
  'billing.feature_conversation_retention': '{{count}}-day conversation retention',
  'billing.feature_max_resolution': '{{resolution}} maximum resolution',
  'billing.feature_hd_download': 'HD downloads',
  'billing.feature_priority_generation': 'Priority generation',
  'billing.feature_commercial_use': 'Commercial-use rights',
  'billing.feature_no_watermark': 'Watermark-free downloads',
  'billing.feature_sharing': 'Shareable visual pages',
  'billing.feature_batch_import': 'CSV and XLSX batch import',
  'billing.feature_custom_branding': 'Custom branding',
  'billing.feature_human_handover': 'Human handover',
  'billing.feature_lead_export': 'Lead export',
  'billing.feature_webhooks': 'Assistant webhooks',
  'billing.feature_ai_sales_assistant': 'Catalog-aware AI assistant and lead capture',
  'billing.feature_custom_widget': 'Custom Widget styling',
  'billing.feature_visual_generation_from_chat': 'Visual generation from chat',
  'billing.feature_advanced_analytics': 'Advanced Assistant analytics',
  'billing.feature_advanced_routing': 'Advanced conversation routing',
  'billing.feature_priority_models': 'Priority model routing',
  'billing.feature_api_access': 'API access ready',
  'billing.feature_assistant_addon_eligible': 'AI Visual Sales Assistant Add-on eligible',
  'billing.preparing': 'Preparing…',
  'billing.start_trial': 'Start {{days}}-day trial',
  'billing.account_kicker': 'Account billing',
  'billing.account_title': 'Plan, usage and payment',
  'billing.opening': 'Opening…',
  'billing.manage_in_paddle': 'Manage in Paddle',
  'billing.loading_account_title': 'Loading billing',
  'billing.loading_account_body': 'Reading the webhook-synchronized account state…',
  'billing.no_plan': 'No plan',
  'billing.choose_plan': 'Choose a plan',
  'billing.access_active': 'Access is active and synchronized from Paddle.',
  'billing.access_limited': 'Access is limited: {{reason}}.',
  'billing.compare_plans': 'Compare plans',
  'billing.cancel_plan': 'Cancel plan',
  'billing.renewal': 'Renewal',
  'billing.credits': 'Credits',
  'billing.addon': 'Add-on',
  'billing.addon_body': 'Managed independently from the base plan.',
  'billing.checkout_reference_missing': 'Checkout reference is missing.',
  'billing.payment_verification_failed': 'Unable to verify payment.',
  'billing.payment_unverified_title': 'We could not verify this payment',
  'billing.payment_confirmed_title': 'Payment confirmed',
  'billing.payment_confirmed_body': 'Paddle’s signed webhook has synchronized your purchase and benefits.',
  'billing.payment_incomplete_title': 'Payment was not completed',
  'billing.payment_incomplete_body': 'No benefits were granted. You can retry safely from pricing.',
  'billing.payment_pending_title': 'Confirming your payment',
  'billing.payment_pending_body': 'The checkout return is not treated as proof. We are waiting for Paddle’s signed webhook…',
  'billing.view_billing': 'View billing',
  'billing.back_to_pricing': 'Back to pricing',
  'billing.past_due_title': 'Payment needs attention',
  'billing.past_due_body': 'Your account is in its limited grace period. Update the payment method in Paddle to restore uninterrupted access.',
  'billing.review_billing': 'Review billing',
  'billing.cancellation_failed': 'Cancellation failed.',
  'billing.cancel_kicker': 'Before you go',
  'billing.cancellation_requested': 'Cancellation requested',
  'billing.cancel_title': 'Cancel {{brand}}?',
  'billing.cancellation_scheduled_body': 'Paddle accepted the request. The billing page will update after the signed webhook arrives.',
  'billing.cancellation_body': 'Your plan stays active until the end of the current billing period. You can reverse this in Paddle before then.',
  'billing.keep_plan': 'Keep my plan',
  'billing.scheduling': 'Scheduling…',
  'billing.cancel_at_period_end': 'Cancel at period end',
  'widget.send': 'Send',
  'widget.close': 'Close chat',
  'widget.message_placeholder': 'Write a message…',
};

export const appMessageCatalogs: Record<AppI18nNamespace, TranslationCatalog> = {
  platform: {
    'en-US': {
      'navigation.dashboard': 'Dashboard',
      'navigation.applications': 'Applications',
      'navigation.users': 'Users',
      'state.console_unavailable': 'Platform Console unavailable',
      'state.access_required': 'Platform Administrator access required',
    },
  },
  home_visual: {
    'en-US': {
      'navigation.dashboard': 'Dashboard',
      'navigation.projects': 'Projects',
      'navigation.account': 'Account',
      'navigation.room_redesign': 'Room Redesign',
      'navigation.virtual_staging': 'Virtual Staging',
      'navigation.declutter': 'Declutter',
      'navigation.examples': 'Examples',
      'navigation.pricing': 'Pricing',
      'navigation.login': 'Log in',
      'navigation.start_free': 'Start free',
      'navigation.open_navigation': 'Open navigation',
      'navigation.explore': 'Explore',
      'navigation.trust': 'Trust',
      'navigation.terms': 'Terms',
      'navigation.privacy': 'Privacy',
      'navigation.ai_disclaimer': 'AI disclaimer',
      'navigation.imagine': 'Imagine',
      'navigation.inspiration': 'Inspiration',
      'navigation.analytics': 'Analytics',
      'navigation.credits': 'credits',
      'navigation.sign_out': 'Sign out',
      'navigation.language': 'Language',
      'navigation.marketing': 'Home Visual marketing navigation',
      'navigation.application': 'Home Visual application navigation',
      'marketing.tagline': 'Imagine your room before you change it.',
      'marketing.footer_notice': 'AI-generated visualizations are illustrative and should not be treated as construction, safety, or property advice.',
      'marketing.hero_image_alt': 'The same empty living room before and warmly furnished after',
      'marketing.hero_before': 'Before', 'marketing.hero_after': 'After',
      'marketing.hero_caption': 'AI-generated visualization',
      'marketing.home_eyebrow': 'A clearer way to plan your space',
      'marketing.home_title': 'Imagine your room before you change it.',
      'marketing.home_body': 'Upload a real room photo and explore redesigns, virtual staging, and calmer spaces in minutes—without changing the architecture that makes it yours.',
      'marketing.home_upload': 'Upload a room', 'marketing.home_examples': 'See real examples',
      'marketing.home_benefits': 'Trial benefits', 'marketing.home_credit': '10 free image credits',
      'marketing.home_no_card': 'No card required', 'marketing.home_private': 'Your photos stay private',
      'marketing.home_audience': 'Made for real homes and real decisions',
      'marketing.audience_renovating': 'Renovating', 'marketing.audience_renting': 'Renting',
      'marketing.audience_hosting': 'Hosting', 'marketing.audience_real_estate': 'Real estate',
      'marketing.audience_exploring': 'Exploring',
      'marketing.home_redesign_body': 'Try a new style, palette, or furniture direction while preserving the shape of your room.',
      'marketing.home_redesign_cta': 'Explore redesign',
      'marketing.home_staging_body': 'Turn an empty room into a believable, furnished space for planning or presentation.',
      'marketing.home_staging_cta': 'Explore staging',
      'marketing.home_declutter_body': 'Visualize a calmer room by removing visual noise while keeping doors, windows, and fixed structure intact.',
      'marketing.home_declutter_cta': 'Explore decluttering',
      'marketing.home_how_eyebrow': 'From photo to possibility',
      'marketing.home_how_title': 'Three thoughtful steps. One room you recognize.',
      'marketing.home_step_one_title': 'Upload your room', 'marketing.home_step_one_body': 'JPEG, PNG, or WebP. Your original remains private.',
      'marketing.home_step_two_title': 'Choose your direction', 'marketing.home_step_two_body': 'Select a task, room type, style, and creativity level.',
      'marketing.home_step_three_title': 'Compare the result', 'marketing.home_step_three_body': 'Review before and after, then refine when you are ready.',
      'marketing.home_cta_eyebrow': '10 image credits included',
      'marketing.home_cta_title': 'See your first possibility today.',
      'marketing.home_cta_action': 'Start your free trial',
      'marketing.feature_process_eyebrow': 'How it works',
      'marketing.feature_process_title': 'Clear choices, private inputs, useful results.',
      'marketing.feature_process_body': 'Every result is an AI-generated visualization. Always verify measurements, materials, and feasibility before making a purchase or construction decision.',
      'marketing.feature_redesign_eyebrow': 'A fresh point of view',
      'marketing.feature_redesign_lead': 'Explore new styles, colors, and furniture layouts in the room you already know.',
      'marketing.feature_redesign_point_one': 'Keep windows, doors, and fixed structure recognizable',
      'marketing.feature_redesign_point_two': 'Try different interior styles before buying',
      'marketing.feature_redesign_point_three': 'Adjust creativity to stay close or explore further',
      'marketing.feature_redesign_step_one': 'Upload your current room',
      'marketing.feature_redesign_step_two': 'Choose a room and style',
      'marketing.feature_redesign_step_three': 'Compare and refine',
      'marketing.feature_staging_eyebrow': 'Help empty rooms tell a story',
      'marketing.feature_staging_lead': 'Furnish a clean, empty room with realistic layouts that respect its scale and architecture.',
      'marketing.feature_staging_point_one': 'Add believable furniture without structural edits',
      'marketing.feature_staging_point_two': 'Explore layouts for listings, rentals, or planning',
      'marketing.feature_staging_point_three': 'Create an inviting sense of scale and purpose',
      'marketing.feature_staging_step_one': 'Upload an empty room',
      'marketing.feature_staging_step_two': 'Choose its purpose and style',
      'marketing.feature_staging_step_three': 'Review staged options',
      'marketing.feature_declutter_eyebrow': 'Make space for a calmer view',
      'marketing.feature_declutter_lead': 'Remove visual noise and see the room clean and calm without changing what is built in.',
      'marketing.feature_declutter_point_one': 'Remove clutter while preserving permanent fixtures',
      'marketing.feature_declutter_point_two': 'Keep the room recognizable and naturally lit',
      'marketing.feature_declutter_point_three': 'Use the result as a practical organizing reference',
      'marketing.feature_declutter_step_one': 'Upload the lived-in room',
      'marketing.feature_declutter_step_two': 'Mark what should stay',
      'marketing.feature_declutter_step_three': 'Review a calmer version',
      'marketing.examples_eyebrow': 'Real rooms, new possibilities',
      'marketing.examples_title': 'Examples created with Home Visual.',
      'marketing.examples_body': 'Use these results as inspiration, then upload your own room for a private visualization.',
      'marketing.examples_filter': 'Filter examples', 'marketing.examples_all': 'All',
      'marketing.example_warm_living_room': 'Warm living room',
      'marketing.example_calm_rental_bedroom': 'Calm rental bedroom',
      'marketing.example_city_apartment': 'Furnished city apartment',
      'marketing.example_property_listing': 'Welcoming property listing',
      'marketing.example_family_room': 'Clear family room',
      'marketing.example_home_office': 'Quiet home office',
      'marketing.examples_cta_title': 'Your room is the best place to begin.',
      'marketing.pricing_eyebrow': 'Start without a card',
      'marketing.pricing_title': 'Begin free. Choose more only when you need it.',
      'marketing.pricing_body': 'Every new Home Visual account receives a private 7-day trial and 10 image credits. Paid prices below come directly from the live billing catalog.',
      'marketing.pricing_trial': 'Free trial', 'marketing.pricing_credits': '10 image credits',
      'marketing.pricing_trial_detail': 'Standard visualizations · Private projects · 7 days',
      'marketing.pricing_action': 'Get started free',
      'project.updated': 'Updated {{date}}',
      'billing.credits_available': '{{count}} credits available',
    },
    'zh-CN': {
      'navigation.dashboard': '控制台', 'navigation.projects': '项目', 'navigation.account': '账户',
      'navigation.room_redesign': '空间改造', 'navigation.virtual_staging': '虚拟布置', 'navigation.declutter': '整理空间',
      'navigation.examples': '案例', 'navigation.pricing': '价格', 'navigation.login': '登录', 'navigation.start_free': '免费开始',
      'navigation.open_navigation': '打开导航', 'navigation.explore': '探索', 'navigation.trust': '信任与安全',
      'navigation.terms': '服务条款', 'navigation.privacy': '隐私', 'navigation.ai_disclaimer': 'AI 免责声明',
      'navigation.imagine': '开始构想', 'navigation.inspiration': '灵感', 'navigation.analytics': '分析',
      'navigation.credits': '积分', 'navigation.sign_out': '退出登录',
      'navigation.language': '语言',
      'navigation.marketing': 'Home Visual 市场导航', 'navigation.application': 'Home Visual 应用导航',
      'marketing.tagline': '在改变房间前，先想象它的样子。',
      'marketing.footer_notice': 'AI 生成的可视化内容仅供参考，不应被视为施工、安全或房产建议。',
      'marketing.hero_image_alt': '同一间空客厅在布置前后形成的对比',
      'marketing.hero_before': '改造前', 'marketing.hero_after': '改造后',
      'marketing.hero_caption': 'AI 生成的可视化效果',
      'marketing.home_eyebrow': '更清晰地规划你的空间',
      'marketing.home_title': '在改变房间前，先想象它的样子。',
      'marketing.home_body': '上传真实房间照片，几分钟内探索空间改造、虚拟布置和更整洁的方案，同时保留属于你的建筑结构。',
      'marketing.home_upload': '上传房间照片', 'marketing.home_examples': '查看真实案例',
      'marketing.home_benefits': '试用权益', 'marketing.home_credit': '含 10 个免费图片积分',
      'marketing.home_no_card': '无需银行卡', 'marketing.home_private': '你的照片始终私密',
      'marketing.home_audience': '为真实家居与真实决策而设计',
      'marketing.audience_renovating': '装修中', 'marketing.audience_renting': '租住中',
      'marketing.audience_hosting': '待客与短租', 'marketing.audience_real_estate': '房地产',
      'marketing.audience_exploring': '探索灵感',
      'marketing.home_redesign_body': '在保留房间格局的同时，尝试新的风格、配色或家具方向。',
      'marketing.home_redesign_cta': '探索空间改造',
      'marketing.home_staging_body': '把空房间转化为可信、已布置的空间，用于规划或展示。',
      'marketing.home_staging_cta': '探索虚拟布置',
      'marketing.home_declutter_body': '移除视觉杂乱，同时保留门窗和固定结构，预览更平静的空间。',
      'marketing.home_declutter_cta': '探索空间整理',
      'marketing.home_how_eyebrow': '从照片到可能',
      'marketing.home_how_title': '三步细致操作，仍是你熟悉的房间。',
      'marketing.home_step_one_title': '上传你的房间', 'marketing.home_step_one_body': '支持 JPEG、PNG 或 WebP，原图保持私密。',
      'marketing.home_step_two_title': '选择方向', 'marketing.home_step_two_body': '选择任务、房型、风格与创意程度。',
      'marketing.home_step_three_title': '对比结果', 'marketing.home_step_three_body': '查看前后对比，并在准备好时继续细化。',
      'marketing.home_cta_eyebrow': '含 10 个图片积分',
      'marketing.home_cta_title': '今天就看见你的第一个可能。',
      'marketing.home_cta_action': '开始免费试用',
      'marketing.feature_process_eyebrow': '使用方式',
      'marketing.feature_process_title': '清晰选择、私密输入、实用结果。',
      'marketing.feature_process_body': '每个结果都是 AI 生成的可视化效果。在购买或施工前，请始终核实尺寸、材料和可行性。',
      'marketing.feature_redesign_eyebrow': '换一个新的视角',
      'marketing.feature_redesign_lead': '在你熟悉的房间中探索新的风格、颜色和家具布局。',
      'marketing.feature_redesign_point_one': '让门窗和固定结构保持可识别',
      'marketing.feature_redesign_point_two': '在购买前尝试不同的室内风格',
      'marketing.feature_redesign_point_three': '调节创意程度，贴近现实或进一步探索',
      'marketing.feature_redesign_step_one': '上传当前房间',
      'marketing.feature_redesign_step_two': '选择房型和风格',
      'marketing.feature_redesign_step_three': '对比并细化',
      'marketing.feature_staging_eyebrow': '让空房间讲述一个故事',
      'marketing.feature_staging_lead': '为干净的空房间布置真实可信的布局，并尊重其尺度和建筑结构。',
      'marketing.feature_staging_point_one': '添加可信家具，不改动建筑结构',
      'marketing.feature_staging_point_two': '探索适合房源、出租或规划的布局',
      'marketing.feature_staging_point_three': '营造有吸引力的尺度感和用途感',
      'marketing.feature_staging_step_one': '上传空房间',
      'marketing.feature_staging_step_two': '选择用途和风格',
      'marketing.feature_staging_step_three': '查看布置方案',
      'marketing.feature_declutter_eyebrow': '为更平静的视野留出空间',
      'marketing.feature_declutter_lead': '移除视觉杂乱，看见整洁平静的房间，而不改变固定结构。',
      'marketing.feature_declutter_point_one': '整理杂物，同时保留永久固定装置',
      'marketing.feature_declutter_point_two': '保持房间可识别且自然采光',
      'marketing.feature_declutter_point_three': '将结果作为实用的整理参考',
      'marketing.feature_declutter_step_one': '上传正在使用的房间',
      'marketing.feature_declutter_step_two': '标记需要保留的内容',
      'marketing.feature_declutter_step_three': '查看更平静的版本',
      'marketing.examples_eyebrow': '真实房间，新的可能',
      'marketing.examples_title': '由 Home Visual 创建的案例。',
      'marketing.examples_body': '将这些结果作为灵感，然后上传自己的房间，获得私密可视化效果。',
      'marketing.examples_filter': '筛选案例', 'marketing.examples_all': '全部',
      'marketing.example_warm_living_room': '温暖的客厅',
      'marketing.example_calm_rental_bedroom': '宁静的出租卧室',
      'marketing.example_city_apartment': '已布置的城市公寓',
      'marketing.example_property_listing': '温馨的房源展示',
      'marketing.example_family_room': '整洁的家庭活动室',
      'marketing.example_home_office': '安静的居家办公室',
      'marketing.examples_cta_title': '从自己的房间开始，最合适不过。',
      'marketing.pricing_eyebrow': '无需银行卡即可开始',
      'marketing.pricing_title': '先免费开始，需要时再选择更多。',
      'marketing.pricing_body': '每个新的 Home Visual 账户均享有私密的 7 天试用和 10 个图片积分。下方付费价格直接来自实时计费目录。',
      'marketing.pricing_trial': '免费试用', 'marketing.pricing_credits': '10 个图片积分',
      'marketing.pricing_trial_detail': '标准可视化 · 私密项目 · 7 天',
      'marketing.pricing_action': '免费开始使用',
    },
    'es-ES': {
      'navigation.dashboard': 'Panel', 'navigation.projects': 'Proyectos', 'navigation.account': 'Cuenta',
      'navigation.room_redesign': 'Rediseño de habitación', 'navigation.virtual_staging': 'Decoración virtual', 'navigation.declutter': 'Ordenar',
      'navigation.examples': 'Ejemplos', 'navigation.pricing': 'Precios', 'navigation.login': 'Iniciar sesión', 'navigation.start_free': 'Empezar gratis',
      'navigation.open_navigation': 'Abrir navegación', 'navigation.explore': 'Explorar', 'navigation.trust': 'Confianza',
      'navigation.terms': 'Términos', 'navigation.privacy': 'Privacidad', 'navigation.ai_disclaimer': 'Aviso de IA',
      'navigation.imagine': 'Imaginar', 'navigation.inspiration': 'Inspiración', 'navigation.analytics': 'Analítica',
      'navigation.credits': 'créditos', 'navigation.sign_out': 'Cerrar sesión',
      'navigation.language': 'Idioma',
      'navigation.marketing': 'Navegación de Home Visual', 'navigation.application': 'Navegación de la aplicación Home Visual',
      'marketing.tagline': 'Imagina tu habitación antes de cambiarla.',
      'marketing.footer_notice': 'Las visualizaciones generadas por IA son ilustrativas y no constituyen asesoramiento de construcción, seguridad o propiedad.',
      'marketing.hero_image_alt': 'La misma sala vacía antes y después de amueblarla con calidez',
      'marketing.hero_before': 'Antes', 'marketing.hero_after': 'Después',
      'marketing.hero_caption': 'Visualización generada por IA',
      'marketing.home_eyebrow': 'Una forma más clara de planear tu espacio',
      'marketing.home_title': 'Imagina tu habitación antes de cambiarla.',
      'marketing.home_body': 'Sube una foto real de tu habitación y explora rediseños, decoración virtual y espacios más tranquilos en minutos, sin cambiar la arquitectura que la hace tuya.',
      'marketing.home_upload': 'Subir una habitación', 'marketing.home_examples': 'Ver ejemplos reales',
      'marketing.home_benefits': 'Beneficios de prueba', 'marketing.home_credit': '10 créditos de imagen gratis',
      'marketing.home_no_card': 'No se requiere tarjeta', 'marketing.home_private': 'Tus fotos permanecen privadas',
      'marketing.home_audience': 'Para hogares reales y decisiones reales',
      'marketing.audience_renovating': 'Renovación', 'marketing.audience_renting': 'Alquiler',
      'marketing.audience_hosting': 'Alojamiento', 'marketing.audience_real_estate': 'Inmobiliaria',
      'marketing.audience_exploring': 'Exploración',
      'marketing.home_redesign_body': 'Prueba un nuevo estilo, paleta o dirección de mobiliario conservando la forma de tu habitación.',
      'marketing.home_redesign_cta': 'Explorar rediseño',
      'marketing.home_staging_body': 'Convierte una habitación vacía en un espacio amueblado y creíble para planificar o presentar.',
      'marketing.home_staging_cta': 'Explorar decoración',
      'marketing.home_declutter_body': 'Visualiza una habitación más tranquila eliminando ruido visual y conservando puertas, ventanas y estructura fija.',
      'marketing.home_declutter_cta': 'Explorar orden',
      'marketing.home_how_eyebrow': 'De la foto a la posibilidad',
      'marketing.home_how_title': 'Tres pasos atentos. Una habitación que reconoces.',
      'marketing.home_step_one_title': 'Sube tu habitación', 'marketing.home_step_one_body': 'JPEG, PNG o WebP. Tu original permanece privado.',
      'marketing.home_step_two_title': 'Elige tu dirección', 'marketing.home_step_two_body': 'Selecciona una tarea, tipo de habitación, estilo y nivel de creatividad.',
      'marketing.home_step_three_title': 'Compara el resultado', 'marketing.home_step_three_body': 'Revisa el antes y después, y ajusta cuando estés listo.',
      'marketing.home_cta_eyebrow': '10 créditos de imagen incluidos',
      'marketing.home_cta_title': 'Ve hoy tu primera posibilidad.',
      'marketing.home_cta_action': 'Comienza tu prueba gratis',
      'marketing.feature_process_eyebrow': 'Cómo funciona',
      'marketing.feature_process_title': 'Opciones claras, entradas privadas, resultados útiles.',
      'marketing.feature_process_body': 'Cada resultado es una visualización generada por IA. Verifica siempre medidas, materiales y viabilidad antes de comprar o construir.',
      'marketing.feature_redesign_eyebrow': 'Un punto de vista renovado',
      'marketing.feature_redesign_lead': 'Explora nuevos estilos, colores y distribuciones de muebles en la habitación que ya conoces.',
      'marketing.feature_redesign_point_one': 'Mantén reconocibles ventanas, puertas y estructura fija',
      'marketing.feature_redesign_point_two': 'Prueba estilos de interior antes de comprar',
      'marketing.feature_redesign_point_three': 'Ajusta la creatividad para mantenerte cerca o explorar más',
      'marketing.feature_redesign_step_one': 'Sube tu habitación actual',
      'marketing.feature_redesign_step_two': 'Elige habitación y estilo',
      'marketing.feature_redesign_step_three': 'Compara y ajusta',
      'marketing.feature_staging_eyebrow': 'Ayuda a las habitaciones vacías a contar una historia',
      'marketing.feature_staging_lead': 'Amuebla una habitación vacía y limpia con distribuciones realistas que respetan su escala y arquitectura.',
      'marketing.feature_staging_point_one': 'Añade muebles creíbles sin cambios estructurales',
      'marketing.feature_staging_point_two': 'Explora distribuciones para anuncios, alquileres o planificación',
      'marketing.feature_staging_point_three': 'Crea una sensación atractiva de escala y propósito',
      'marketing.feature_staging_step_one': 'Sube una habitación vacía',
      'marketing.feature_staging_step_two': 'Elige su propósito y estilo',
      'marketing.feature_staging_step_three': 'Revisa las opciones decoradas',
      'marketing.feature_declutter_eyebrow': 'Haz espacio para una vista más tranquila',
      'marketing.feature_declutter_lead': 'Elimina ruido visual y observa la habitación limpia y tranquila sin cambiar lo construido.',
      'marketing.feature_declutter_point_one': 'Quita el desorden conservando elementos permanentes',
      'marketing.feature_declutter_point_two': 'Mantén la habitación reconocible y con luz natural',
      'marketing.feature_declutter_point_three': 'Usa el resultado como referencia práctica para organizar',
      'marketing.feature_declutter_step_one': 'Sube la habitación habitada',
      'marketing.feature_declutter_step_two': 'Marca lo que debe quedarse',
      'marketing.feature_declutter_step_three': 'Revisa una versión más tranquila',
      'marketing.examples_eyebrow': 'Habitaciones reales, nuevas posibilidades',
      'marketing.examples_title': 'Ejemplos creados con Home Visual.',
      'marketing.examples_body': 'Usa estos resultados como inspiración y luego sube tu propia habitación para una visualización privada.',
      'marketing.examples_filter': 'Filtrar ejemplos', 'marketing.examples_all': 'Todos',
      'marketing.example_warm_living_room': 'Sala de estar cálida',
      'marketing.example_calm_rental_bedroom': 'Dormitorio de alquiler tranquilo',
      'marketing.example_city_apartment': 'Apartamento urbano amueblado',
      'marketing.example_property_listing': 'Anuncio inmobiliario acogedor',
      'marketing.example_family_room': 'Sala familiar despejada',
      'marketing.example_home_office': 'Oficina en casa tranquila',
      'marketing.examples_cta_title': 'Tu habitación es el mejor lugar para empezar.',
      'marketing.pricing_eyebrow': 'Empieza sin tarjeta',
      'marketing.pricing_title': 'Empieza gratis. Elige más solo cuando lo necesites.',
      'marketing.pricing_body': 'Cada nueva cuenta de Home Visual recibe una prueba privada de 7 días y 10 créditos de imagen. Los precios de pago siguientes provienen directamente del catálogo de facturación activo.',
      'marketing.pricing_trial': 'Prueba gratis', 'marketing.pricing_credits': '10 créditos de imagen',
      'marketing.pricing_trial_detail': 'Visualizaciones estándar · Proyectos privados · 7 días',
      'marketing.pricing_action': 'Empezar gratis',
    },
  },
  product_visual: {
    'en-US': {
      'navigation.dashboard': 'Dashboard',
      'navigation.catalog': 'Catalog',
      'navigation.visual_studio': 'Visual Studio',
      'navigation.assistant': 'AI Sales Assistant',
      'catalog.products_available': '{{count}} products available on the current plan.',
    },
  },
  sales_agent: {
    'en-US': {
      'navigation.dashboard': 'Dashboard',
      'navigation.integrations': 'Integrations',
      'navigation.conversations': 'Conversations',
      'integration.connect': 'Connect Product Visual',
      'integration.revoke': 'Revoke access',
    },
  },
  tarot: {
    'en-US': {
      'navigation.dashboard': 'Dashboard',
      'navigation.readings': 'Readings',
      'navigation.journal': 'Journal',
      'navigation.account': 'Account',
      'navigation.how_it_works': 'How it works', 'navigation.spreads': 'Spreads', 'navigation.pricing': 'Pricing',
      'navigation.safety': 'Safety', 'navigation.login': 'Log in', 'navigation.begin_free': 'Begin free',
      'navigation.open_navigation': 'Open navigation', 'navigation.menu': 'Menu', 'navigation.explore': 'Explore',
      'navigation.trust': 'Trust', 'navigation.safety_notice': 'Safety notice', 'navigation.privacy': 'Privacy',
      'navigation.terms': 'Terms', 'navigation.overview': 'Overview', 'navigation.deck': 'Deck',
      'navigation.daily': 'Daily', 'navigation.history': 'History', 'navigation.growth': 'Growth',
      'navigation.plans': 'Plans', 'navigation.daily_draw': 'Daily draw', 'navigation.reading_history': 'Reading history',
      'navigation.private_journal': 'Private journal', 'navigation.credits': 'reading credits', 'navigation.marketing': 'Lumen Tarot navigation',
      'navigation.language': 'Language',
      'navigation.application': 'Lumen Tarot application navigation', 'navigation.mobile_application': 'Lumen Tarot mobile navigation',
      'marketing.tagline': 'A quieter space for questions, reflection, and growth.',
      'marketing.footer_notice': 'For entertainment and self-reflection. Lumen Tarot is not medical, mental-health, legal, or financial advice and does not predict the future with certainty.',
      'marketing.safety_strip_title': 'Grounded reflection, never certain prediction.',
      'marketing.safety_strip_body': 'Use your own judgment and seek qualified support for important health, safety, legal, or financial decisions.',
      'marketing.safety_strip_link': 'Read our safety promise',
      'marketing.safety_eyebrow': 'A gentle reminder',
      'marketing.safety_title': 'Reflection is not professional advice.',
      'marketing.safety_body': 'Lumen Tarot is designed for entertainment, self-reflection, emotional organization, and growth inspiration.',
      'marketing.safety_medical_title': 'Not medical or mental-health advice',
      'marketing.safety_medical_body': 'Do not stop medication, diagnose a condition, or replace a qualified clinician based on a reading.',
      'marketing.safety_legal_title': 'Not legal or financial advice',
      'marketing.safety_legal_body': 'A reading cannot determine whether an action is lawful or promise an investment result.',
      'marketing.safety_prediction_title': 'Not a certain prediction',
      'marketing.safety_prediction_body': 'Cards do not determine death, disease, disaster, another person’s private thoughts, or an inevitable future.',
      'marketing.safety_urgent_title': 'When something feels urgent or unsafe',
      'marketing.safety_urgent_body': 'Pause the reading and contact appropriate local emergency or qualified professional support. Lumen Tarot will prioritize real-world safety over an interpretive answer.',
      'marketing.safety_back': 'Back home', 'marketing.safety_action': 'I understand · begin free',
      'marketing.home_eyebrow': 'For entertainment and self-reflection',
      'marketing.home_title': 'A quieter way to hear yourself.',
      'marketing.home_body': 'Bring a question. Draw with intention. Receive a thoughtful, grounded reflection designed to help you notice what already matters.',
      'marketing.home_begin': 'Begin a reflection', 'marketing.home_how': 'See how it works',
      'marketing.home_benefits': 'Starting benefits', 'marketing.home_credit': '3 reading credits included',
      'marketing.home_no_card': 'No card required', 'marketing.home_private': 'Private by default',
      'marketing.home_card_backs': 'Celestial Tarot card backs',
      'marketing.home_intent': 'Made for the questions that deserve a little room.',
      'marketing.topic_self_discovery': 'Self-discovery', 'marketing.topic_relationships': 'Relationships',
      'marketing.topic_career': 'Career & purpose', 'marketing.topic_creativity': 'Creativity',
      'marketing.topic_transitions': 'Life transitions',
      'marketing.home_ritual_eyebrow': 'A calm ritual, made practical',
      'marketing.home_ritual_title': 'Reflect without giving away your agency.',
      'marketing.home_step_one_title': 'Choose a question', 'marketing.home_step_one_body': 'Set an intention without forcing a yes-or-no prediction.',
      'marketing.home_step_two_title': 'Draw with intention', 'marketing.home_step_two_body': 'Your cards will be selected by a secure, reproducible draw.',
      'marketing.home_step_three_title': 'Reflect with guidance', 'marketing.home_step_three_body': 'Explore themes, questions, and grounded next steps.',
      'marketing.home_spread_eyebrow': 'Different lenses, different questions',
      'marketing.home_spread_title': 'Start with a spread that fits the moment.',
      'marketing.home_spread_body': 'From one clear prompt to a deeper view of relationships or purpose, every spread keeps the final judgment with you.',
      'marketing.home_spread_action': 'Explore all spreads', 'marketing.home_spread_alt': 'Single-card spread',
      'marketing.home_cta_eyebrow': 'Your first three reflections are included',
      'marketing.home_cta_title': 'Make space for the question.',
      'marketing.home_cta_body': 'No payment card required. Your Tarot account stays independent.',
      'marketing.home_cta_action': 'Begin free',
      'marketing.how_eyebrow': 'How Lumen Tarot works',
      'marketing.how_title': 'A reflection belongs to you—not to an algorithm.',
      'marketing.how_body': 'Lumen Tarot combines a secure card draw with structured guidance while keeping your choice, context, and judgment at the center.',
      'marketing.how_step_one_body': 'Name the situation you want to explore. Open questions leave more room for useful reflection.',
      'marketing.how_step_one_prompt': 'What would help me move through this transition with care?',
      'marketing.how_step_two_body': 'The server performs the draw. The AI never chooses which cards you receive.',
      'marketing.how_step_two_prompt': 'Secure draw · upright or reversed · saved once',
      'marketing.how_step_three_body': 'Receive themes, reflection questions, and possible actions—not a fixed prediction.',
      'marketing.how_step_three_prompt': 'Notice · question · choose your next step',
      'marketing.how_draw_title': 'The draw is separate from the interpretation',
      'marketing.how_draw_body': 'Cards are selected securely and stored before any AI interpretation begins.',
      'marketing.how_private_title': 'Your reflections are private',
      'marketing.how_private_body': 'Tarot questions never become Home Visual, Product Visual, or business-customer data.',
      'marketing.how_grounded_title': 'Guidance stays grounded',
      'marketing.how_grounded_body': 'Safety rules prevent medical, legal, financial, and certain future claims.',
      'marketing.how_cta_title': 'Ready to begin with one thoughtful question?',
      'marketing.how_cta_action': 'Create your private account',
      'marketing.spreads_eyebrow': 'Choose a spread',
      'marketing.spreads_title': 'Different layouts for different questions.',
      'marketing.spreads_body': 'A spread gives your reflection structure. It never removes your agency or guarantees an outcome.',
      'marketing.spread_single_title': 'Single Card', 'marketing.spread_single_subtitle': 'One clear lens',
      'marketing.spread_single_body': 'A concise reflection for the energy, theme, or question in front of you.',
      'marketing.spread_three_title': 'Three Card', 'marketing.spread_three_subtitle': 'Past · Present · Possibility',
      'marketing.spread_three_body': 'Notice what shaped the moment, what is active now, and what you may choose next.',
      'marketing.spread_relationship_title': 'Relationships', 'marketing.spread_relationship_subtitle': 'Connection · Pattern · Care',
      'marketing.spread_relationship_body': 'Reflect on a relationship dynamic without predicting another person’s thoughts.',
      'marketing.spread_career_title': 'Career & Purpose', 'marketing.spread_career_subtitle': 'Strength · Tension · Direction',
      'marketing.spread_career_body': 'Explore your work, values, and possible next steps without financial promises.',
      'marketing.spread_included': 'Included', 'marketing.spread_plus': 'Plus',
      'marketing.spread_action': 'Begin with this spread',
      'marketing.pricing_eyebrow': 'Simple, independent Tarot plans',
      'marketing.pricing_title': 'Choose the space you need to reflect.',
      'marketing.pricing_body': 'Every plan uses Tarot reading credits only. Home Visual and business credits can never be used here. Live prices and trial terms come directly from the secure billing catalog.',
    },
    'zh-CN': {
      'navigation.dashboard': '控制台', 'navigation.readings': '解读', 'navigation.journal': '日记', 'navigation.account': '账户',
      'navigation.how_it_works': '使用方式', 'navigation.spreads': '牌阵', 'navigation.pricing': '价格', 'navigation.safety': '安全说明',
      'navigation.login': '登录', 'navigation.begin_free': '免费开始', 'navigation.open_navigation': '打开导航', 'navigation.menu': '菜单',
      'navigation.explore': '探索', 'navigation.trust': '信任与安全', 'navigation.safety_notice': '安全提示',
      'navigation.privacy': '隐私', 'navigation.terms': '服务条款', 'navigation.overview': '概览', 'navigation.deck': '牌组',
      'navigation.daily': '每日', 'navigation.history': '历史', 'navigation.growth': '成长', 'navigation.plans': '方案',
      'navigation.daily_draw': '每日抽牌', 'navigation.reading_history': '解读历史', 'navigation.private_journal': '私人日记',
      'navigation.credits': '解读积分', 'navigation.marketing': 'Lumen Tarot 导航',
      'navigation.language': '语言',
      'navigation.application': 'Lumen Tarot 应用导航', 'navigation.mobile_application': 'Lumen Tarot 移动导航',
      'marketing.tagline': '为问题、反思与成长留出一处安静空间。',
      'marketing.footer_notice': '仅供娱乐与自我反思。Lumen Tarot 不提供医疗、心理健康、法律或财务建议，也不会确定地预测未来。',
      'marketing.safety_strip_title': '脚踏实地地反思，而非确定地预言。',
      'marketing.safety_strip_body': '面对重要的健康、安全、法律或财务决定，请保持独立判断并寻求合格专业人士的支持。',
      'marketing.safety_strip_link': '阅读我们的安全承诺',
      'marketing.safety_eyebrow': '温和提醒',
      'marketing.safety_title': '反思不是专业建议。',
      'marketing.safety_body': 'Lumen Tarot 用于娱乐、自我反思、梳理情绪和启发成长。',
      'marketing.safety_medical_title': '不构成医疗或心理健康建议',
      'marketing.safety_medical_body': '请勿根据一次解读停药、诊断疾病或替代合格临床专业人士的判断。',
      'marketing.safety_legal_title': '不构成法律或财务建议',
      'marketing.safety_legal_body': '一次解读不能判断某项行为是否合法，也不能承诺投资结果。',
      'marketing.safety_prediction_title': '不作确定预测',
      'marketing.safety_prediction_body': '牌卡不能决定死亡、疾病、灾难、他人的私密想法或不可避免的未来。',
      'marketing.safety_urgent_title': '当事情显得紧急或不安全时',
      'marketing.safety_urgent_body': '请暂停解读，并联系合适的本地紧急服务或专业支持。Lumen Tarot 始终将现实世界的安全置于诠释性回答之前。',
      'marketing.safety_back': '返回首页', 'marketing.safety_action': '我已了解 · 免费开始',
      'marketing.home_eyebrow': '仅供娱乐与自我反思',
      'marketing.home_title': '以更安静的方式倾听自己。',
      'marketing.home_body': '带着一个问题而来，有意识地抽牌，获得细致、踏实的反思，帮助你看见已经重要的事。',
      'marketing.home_begin': '开始一次反思', 'marketing.home_how': '查看使用方式',
      'marketing.home_benefits': '起步权益', 'marketing.home_credit': '含 3 个解读积分',
      'marketing.home_no_card': '无需银行卡', 'marketing.home_private': '默认私密',
      'marketing.home_card_backs': '天象塔罗牌背面',
      'marketing.home_intent': '为值得留出一点空间的问题而设计。',
      'marketing.topic_self_discovery': '自我探索', 'marketing.topic_relationships': '关系',
      'marketing.topic_career': '事业与目标', 'marketing.topic_creativity': '创造力',
      'marketing.topic_transitions': '人生转变',
      'marketing.home_ritual_eyebrow': '平静的仪式，也切实可用',
      'marketing.home_ritual_title': '反思，而不交出你的主动权。',
      'marketing.home_step_one_title': '选择一个问题', 'marketing.home_step_one_body': '设定意图，而不是强迫自己得到是或否的预言。',
      'marketing.home_step_two_title': '有意识地抽牌', 'marketing.home_step_two_body': '你的牌将通过安全、可复现的抽牌流程选出。',
      'marketing.home_step_three_title': '在引导中反思', 'marketing.home_step_three_body': '探索主题、问题和踏实的下一步。',
      'marketing.home_spread_eyebrow': '不同视角，不同问题',
      'marketing.home_spread_title': '从适合当下的牌阵开始。',
      'marketing.home_spread_body': '从一个清晰提示到对关系或目标更深入的观察，每种牌阵都把最终判断留给你。',
      'marketing.home_spread_action': '探索全部牌阵', 'marketing.home_spread_alt': '单张牌阵',
      'marketing.home_cta_eyebrow': '包含你的前三次反思',
      'marketing.home_cta_title': '为这个问题留出空间。',
      'marketing.home_cta_body': '无需支付卡。你的塔罗账户保持独立。',
      'marketing.home_cta_action': '免费开始',
      'marketing.how_eyebrow': 'Lumen Tarot 如何运作',
      'marketing.how_title': '反思属于你，而不属于算法。',
      'marketing.how_body': 'Lumen Tarot 将安全抽牌与结构化引导结合，同时始终以你的选择、背景和判断为中心。',
      'marketing.how_step_one_body': '说出你想探索的情境。开放式问题能为有价值的反思留下更多空间。',
      'marketing.how_step_one_prompt': '什么能帮助我更用心地走过这次转变？',
      'marketing.how_step_two_body': '由服务器执行抽牌。AI 永远不会选择你会收到哪张牌。',
      'marketing.how_step_two_prompt': '安全抽牌 · 正位或逆位 · 仅保存一次',
      'marketing.how_step_three_body': '获得主题、反思问题和可能的行动，而不是固定的预言。',
      'marketing.how_step_three_prompt': '观察 · 提问 · 选择你的下一步',
      'marketing.how_draw_title': '抽牌与解读相互独立',
      'marketing.how_draw_body': '在任何 AI 解读开始前，牌都会被安全选出并保存。',
      'marketing.how_private_title': '你的反思保持私密',
      'marketing.how_private_body': '塔罗问题绝不会成为 Home Visual、Product Visual 或企业客户数据。',
      'marketing.how_grounded_title': '引导始终脚踏实地',
      'marketing.how_grounded_body': '安全规则会阻止医疗、法律、财务和确定未来的断言。',
      'marketing.how_cta_title': '准备好从一个细致的问题开始了吗？',
      'marketing.how_cta_action': '创建你的私密账户',
      'marketing.spreads_eyebrow': '选择牌阵',
      'marketing.spreads_title': '不同布局，适合不同问题。',
      'marketing.spreads_body': '牌阵为你的反思提供结构，但不会剥夺你的主动权或保证任何结果。',
      'marketing.spread_single_title': '单张牌', 'marketing.spread_single_subtitle': '一个清晰视角',
      'marketing.spread_single_body': '针对眼前能量、主题或问题的简洁反思。',
      'marketing.spread_three_title': '三张牌', 'marketing.spread_three_subtitle': '过去 · 当下 · 可能',
      'marketing.spread_three_body': '观察什么塑造了当下、什么正在发生，以及你下一步可以如何选择。',
      'marketing.spread_relationship_title': '关系', 'marketing.spread_relationship_subtitle': '连接 · 模式 · 关照',
      'marketing.spread_relationship_body': '反思一段关系的动态，而不预测他人的想法。',
      'marketing.spread_career_title': '事业与目标', 'marketing.spread_career_subtitle': '优势 · 张力 · 方向',
      'marketing.spread_career_body': '探索你的工作、价值观和可能的下一步，不作财务承诺。',
      'marketing.spread_included': '已包含', 'marketing.spread_plus': 'Plus',
      'marketing.spread_action': '从此牌阵开始',
      'marketing.pricing_eyebrow': '简单且独立的 Tarot 套餐',
      'marketing.pricing_title': '选择适合你反思节奏的空间。',
      'marketing.pricing_body': '每个套餐只使用 Tarot 解读积分。Home Visual 和企业积分绝不会在此使用。实时价格和试用条款直接来自安全的计费目录。',
    },
    'es-ES': {
      'navigation.dashboard': 'Panel', 'navigation.readings': 'Lecturas', 'navigation.journal': 'Diario', 'navigation.account': 'Cuenta',
      'navigation.how_it_works': 'Cómo funciona', 'navigation.spreads': 'Tiradas', 'navigation.pricing': 'Precios', 'navigation.safety': 'Seguridad',
      'navigation.login': 'Iniciar sesión', 'navigation.begin_free': 'Empezar gratis', 'navigation.open_navigation': 'Abrir navegación', 'navigation.menu': 'Menú',
      'navigation.explore': 'Explorar', 'navigation.trust': 'Confianza', 'navigation.safety_notice': 'Aviso de seguridad',
      'navigation.privacy': 'Privacidad', 'navigation.terms': 'Términos', 'navigation.overview': 'Resumen', 'navigation.deck': 'Mazo',
      'navigation.daily': 'Diario', 'navigation.history': 'Historial', 'navigation.growth': 'Crecimiento', 'navigation.plans': 'Planes',
      'navigation.daily_draw': 'Carta diaria', 'navigation.reading_history': 'Historial de lecturas', 'navigation.private_journal': 'Diario privado',
      'navigation.credits': 'créditos de lectura', 'navigation.marketing': 'Navegación de Lumen Tarot',
      'navigation.language': 'Idioma',
      'navigation.application': 'Navegación de la aplicación Lumen Tarot', 'navigation.mobile_application': 'Navegación móvil de Lumen Tarot',
      'marketing.tagline': 'Un espacio más tranquilo para preguntas, reflexión y crecimiento.',
      'marketing.footer_notice': 'Para entretenimiento y autorreflexión. Lumen Tarot no ofrece asesoramiento médico, de salud mental, jurídico o financiero, ni predice el futuro con certeza.',
      'marketing.safety_strip_title': 'Reflexión con los pies en la tierra, nunca predicción certera.',
      'marketing.safety_strip_body': 'Usa tu propio juicio y busca apoyo cualificado para decisiones importantes de salud, seguridad, legales o financieras.',
      'marketing.safety_strip_link': 'Lee nuestra promesa de seguridad',
      'marketing.safety_eyebrow': 'Un recordatorio amable',
      'marketing.safety_title': 'La reflexión no es asesoramiento profesional.',
      'marketing.safety_body': 'Lumen Tarot está diseñado para entretenimiento, autorreflexión, organización emocional e inspiración para crecer.',
      'marketing.safety_medical_title': 'No es asesoramiento médico ni de salud mental',
      'marketing.safety_medical_body': 'No suspendas medicación, diagnostiques una afección ni sustituyas a un profesional cualificado basándote en una lectura.',
      'marketing.safety_legal_title': 'No es asesoramiento jurídico ni financiero',
      'marketing.safety_legal_body': 'Una lectura no puede decidir si una acción es legal ni prometer un resultado de inversión.',
      'marketing.safety_prediction_title': 'No es una predicción certera',
      'marketing.safety_prediction_body': 'Las cartas no determinan muerte, enfermedad, desastre, pensamientos privados de otra persona ni un futuro inevitable.',
      'marketing.safety_urgent_title': 'Cuando algo parece urgente o inseguro',
      'marketing.safety_urgent_body': 'Pausa la lectura y contacta con servicios locales de emergencia o apoyo profesional adecuado. Lumen Tarot prioriza la seguridad en el mundo real sobre una respuesta interpretativa.',
      'marketing.safety_back': 'Volver al inicio', 'marketing.safety_action': 'Lo entiendo · empezar gratis',
      'marketing.home_eyebrow': 'Para entretenimiento y autorreflexión',
      'marketing.home_title': 'Una forma más tranquila de escucharte.',
      'marketing.home_body': 'Trae una pregunta. Saca cartas con intención. Recibe una reflexión atenta y realista que te ayude a notar lo que ya importa.',
      'marketing.home_begin': 'Comenzar una reflexión', 'marketing.home_how': 'Ver cómo funciona',
      'marketing.home_benefits': 'Beneficios iniciales', 'marketing.home_credit': '3 créditos de lectura incluidos',
      'marketing.home_no_card': 'No se requiere tarjeta', 'marketing.home_private': 'Privado por defecto',
      'marketing.home_card_backs': 'Reversos de cartas de Tarot celestes',
      'marketing.home_intent': 'Para las preguntas que merecen un poco de espacio.',
      'marketing.topic_self_discovery': 'Autodescubrimiento', 'marketing.topic_relationships': 'Relaciones',
      'marketing.topic_career': 'Carrera y propósito', 'marketing.topic_creativity': 'Creatividad',
      'marketing.topic_transitions': 'Transiciones vitales',
      'marketing.home_ritual_eyebrow': 'Un ritual tranquilo y práctico',
      'marketing.home_ritual_title': 'Reflexiona sin entregar tu autonomía.',
      'marketing.home_step_one_title': 'Elige una pregunta', 'marketing.home_step_one_body': 'Define una intención sin forzar una predicción de sí o no.',
      'marketing.home_step_two_title': 'Saca cartas con intención', 'marketing.home_step_two_body': 'Tus cartas se seleccionan mediante un sorteo seguro y reproducible.',
      'marketing.home_step_three_title': 'Reflexiona con guía', 'marketing.home_step_three_body': 'Explora temas, preguntas y próximos pasos realistas.',
      'marketing.home_spread_eyebrow': 'Lentes distintos, preguntas distintas',
      'marketing.home_spread_title': 'Empieza con una tirada adecuada para el momento.',
      'marketing.home_spread_body': 'Desde una pregunta clara hasta una visión más profunda de relaciones o propósito, cada tirada deja el juicio final en tus manos.',
      'marketing.home_spread_action': 'Explorar todas las tiradas', 'marketing.home_spread_alt': 'Tirada de una carta',
      'marketing.home_cta_eyebrow': 'Tus tres primeras reflexiones están incluidas',
      'marketing.home_cta_title': 'Haz espacio para la pregunta.',
      'marketing.home_cta_body': 'No se requiere tarjeta de pago. Tu cuenta de Tarot permanece independiente.',
      'marketing.home_cta_action': 'Empezar gratis',
      'marketing.how_eyebrow': 'Cómo funciona Lumen Tarot',
      'marketing.how_title': 'Una reflexión te pertenece a ti, no a un algoritmo.',
      'marketing.how_body': 'Lumen Tarot combina una extracción de cartas segura con guía estructurada, manteniendo tu elección, contexto y juicio en el centro.',
      'marketing.how_step_one_body': 'Nombra la situación que quieres explorar. Las preguntas abiertas dejan más espacio para una reflexión útil.',
      'marketing.how_step_one_prompt': '¿Qué me ayudaría a atravesar esta transición con cuidado?',
      'marketing.how_step_two_body': 'El servidor realiza la extracción. La IA nunca elige qué cartas recibes.',
      'marketing.how_step_two_prompt': 'Sorteo seguro · derecho o invertido · guardado una vez',
      'marketing.how_step_three_body': 'Recibe temas, preguntas de reflexión y posibles acciones, no una predicción fija.',
      'marketing.how_step_three_prompt': 'Observa · pregunta · elige tu siguiente paso',
      'marketing.how_draw_title': 'La extracción es independiente de la interpretación',
      'marketing.how_draw_body': 'Las cartas se seleccionan y almacenan de forma segura antes de que comience cualquier interpretación de IA.',
      'marketing.how_private_title': 'Tus reflexiones son privadas',
      'marketing.how_private_body': 'Las preguntas de Tarot nunca se convierten en datos de Home Visual, Product Visual o clientes empresariales.',
      'marketing.how_grounded_title': 'La guía se mantiene realista',
      'marketing.how_grounded_body': 'Las reglas de seguridad evitan afirmaciones médicas, legales, financieras y de futuro certero.',
      'marketing.how_cta_title': '¿Listo para empezar con una pregunta atenta?',
      'marketing.how_cta_action': 'Crea tu cuenta privada',
      'marketing.spreads_eyebrow': 'Elige una tirada',
      'marketing.spreads_title': 'Distribuciones distintas para preguntas distintas.',
      'marketing.spreads_body': 'Una tirada da estructura a tu reflexión. Nunca elimina tu autonomía ni garantiza un resultado.',
      'marketing.spread_single_title': 'Una carta', 'marketing.spread_single_subtitle': 'Una lente clara',
      'marketing.spread_single_body': 'Una reflexión concisa para la energía, el tema o la pregunta frente a ti.',
      'marketing.spread_three_title': 'Tres cartas', 'marketing.spread_three_subtitle': 'Pasado · Presente · Posibilidad',
      'marketing.spread_three_body': 'Observa qué formó el momento, qué está activo ahora y qué puedes elegir después.',
      'marketing.spread_relationship_title': 'Relaciones', 'marketing.spread_relationship_subtitle': 'Conexión · Patrón · Cuidado',
      'marketing.spread_relationship_body': 'Reflexiona sobre una dinámica relacional sin predecir los pensamientos de otra persona.',
      'marketing.spread_career_title': 'Carrera y propósito', 'marketing.spread_career_subtitle': 'Fortaleza · Tensión · Dirección',
      'marketing.spread_career_body': 'Explora tu trabajo, valores y posibles próximos pasos sin promesas financieras.',
      'marketing.spread_included': 'Incluido', 'marketing.spread_plus': 'Plus',
      'marketing.spread_action': 'Comenzar con esta tirada',
      'marketing.pricing_eyebrow': 'Planes de Tarot sencillos e independientes',
      'marketing.pricing_title': 'Elige el espacio que necesitas para reflexionar.',
      'marketing.pricing_body': 'Cada plan usa únicamente créditos de lectura de Tarot. Los créditos de Home Visual y de empresa nunca pueden utilizarse aquí. Los precios y condiciones de prueba se obtienen directamente del catálogo de facturación seguro.',
    },
  },
  auth: {
    'en-US': {
      'error.invalid_credentials': 'The email or password is incorrect.',
      'error.email_unconfirmed': 'Verify your email before signing in.',
      'error.rate_limited': 'Too many attempts. Please wait and try again.',
      'error.password_too_short': 'Use a password with at least 8 characters.',
      'error.password_too_long': 'Use a shorter password. Passwords can be at most 72 UTF-8 bytes.',
      'error.recovery_link_invalid': 'This reset link is invalid or expired. Request a new link.',
      'status.working': 'Working securely…',
      'notice.identity_confirmed': 'Identity confirmed. Choose a new password.',
      'notice.email_verified': 'Email verified. Your application account is ready.',
      'notice.signed_in': 'Signed in to {{brand}}.',
      'notice.check_inbox': 'Check your inbox to verify your email and finish account setup.',
      'notice.account_created': '{{brand}} account created with a 7-day trial.',
      'notice.magic_link_sent': 'Magic link sent. Check your inbox.',
      'notice.password_reset_sent': 'If that email is registered, a reset link is on its way.',
      'notice.password_updated': 'Password updated. You can now log in.',
      'notice.invitation_accepted': 'Invitation accepted for this application.',
      'login.kicker': 'Welcome back', 'login.title': 'Log in', 'login.description': 'Continue to your {{brand}} account.',
      'signup.internal_kicker': 'Controlled access', 'signup.title': 'Sign up',
      'signup.internal_description': 'Platform Console accounts are created through an administrator invitation.',
      'signup.kicker': 'Start your trial', 'signup.description': 'Create an independent {{brand}} account.',
      'forgot.kicker': 'Account recovery', 'forgot.title': 'Forgot password',
      'forgot.description': 'We’ll send a secure reset link without confirming whether an account exists.',
      'verification.kicker': 'Secure activation', 'verification.recovery_title': 'Choose new password',
      'verification.title': 'Email verification', 'verification.recovery_description': 'Set a strong new password for this identity.',
      'verification.description': 'Verify your email to finish creating this application account.',
      'invitation.kicker': 'Application-scoped access', 'invitation.title': 'Accept invitation',
      'invitation.description': 'Confirm the application, account and role before joining.',
      'suspended.kicker': 'Access restricted', 'suspended.title': 'Account suspended',
      'suspended.description': 'This account or membership cannot currently access {{brand}}.',
      'suspended.platform_admin_kicker': 'Restricted surface', 'suspended.platform_admin_title': 'Platform Administrator access required',
      'suspended.platform_admin_description': 'Your identity is valid, but this console is reserved for approved platform operators.',
      'field.email': 'Email', 'field.work_email': 'Work email', 'field.password': 'Password',
      'field.new_password': 'New password', 'field.organization_name': 'Organization name',
      'field.account_name': 'Account name', 'field.your_name': 'Your name',
      'action.login': 'Log in', 'action.signup': 'Sign up', 'action.open_invitation': 'Open invitation',
      'action.send_magic_link': 'Send magic link', 'action.send_reset_link': 'Send reset link',
      'action.update_password': 'Update password', 'action.accept_invitation': 'Accept invitation',
      'action.logout': 'Log out', 'action.back_to_login': 'Back to login',
      'action.forgot_password': 'Forgot password?', 'action.create_account': 'Create account',
      'action.remember_me': 'Remember me', 'action.or': 'or',
      'copy.no_account': 'Don’t have an account?', 'copy.already_registered': 'Already registered?',
      'copy.password_hint': 'Use at least 8 characters with upper and lowercase letters and a number.',
      'copy.consent_prefix': 'I agree to the', 'copy.consent_and': 'and', 'copy.terms': 'Terms',
      'copy.privacy': 'Privacy notice', 'copy.role': 'Role: {{role}}', 'copy.status': 'Status: {{status}}',
      'copy.different_application': 'This invitation belongs to a different application.',
      'copy.invited_identity': 'create an identity with the invited email', 'copy.reference': 'Reference',
      'copy.support': 'Contact support or your organization administrator if this is unexpected.',
      'footer.secure': 'Secure by design · Privacy protected', 'aria.brand': '{{brand}} brand',
    },
    'zh-CN': {
      'error.invalid_credentials': '邮箱或密码不正确。', 'error.email_unconfirmed': '请先验证邮箱再登录。',
      'error.rate_limited': '尝试次数过多，请稍后重试。', 'status.working': '正在安全处理…',
      'error.password_too_short': '请使用至少 8 个字符的密码。', 'error.password_too_long': '请使用更短的密码。密码最多为 72 个 UTF-8 字节。',
      'error.recovery_link_invalid': '重置链接无效或已过期，请重新获取链接。',
      'notice.identity_confirmed': '身份已确认，请设置新密码。', 'notice.email_verified': '邮箱已验证，应用账户已准备完成。',
      'notice.signed_in': '已登录 {{brand}}。', 'notice.check_inbox': '请查收邮箱并验证，以完成账户设置。',
      'notice.account_created': '{{brand}} 账户已创建，含 7 天试用。', 'notice.magic_link_sent': '魔法链接已发送，请查收邮箱。',
      'notice.password_reset_sent': '如该邮箱已注册，重置链接正在发送。', 'notice.password_updated': '密码已更新，现在可以登录。',
      'notice.invitation_accepted': '已接受此应用的邀请。', 'login.kicker': '欢迎回来', 'login.title': '登录',
      'login.description': '继续使用你的 {{brand}} 账户。', 'signup.internal_kicker': '受控访问', 'signup.title': '注册',
      'signup.internal_description': 'Platform Console 账户由管理员邀请创建。', 'signup.kicker': '开始试用',
      'signup.description': '创建独立的 {{brand}} 账户。', 'forgot.kicker': '账户恢复', 'forgot.title': '忘记密码',
      'forgot.description': '我们会发送安全重置链接，而不会确认该账户是否存在。', 'verification.kicker': '安全激活',
      'verification.recovery_title': '设置新密码', 'verification.title': '邮箱验证',
      'verification.recovery_description': '为该身份设置一个安全的新密码。', 'verification.description': '验证邮箱以完成此应用账户的创建。',
      'invitation.kicker': '应用范围访问', 'invitation.title': '接受邀请', 'invitation.description': '加入前请确认应用、账户和角色。',
      'suspended.kicker': '访问受限', 'suspended.title': '账户已暂停', 'suspended.description': '此账户或成员资格目前无法访问 {{brand}}。',
      'suspended.platform_admin_kicker': '受限界面', 'suspended.platform_admin_title': '需要 Platform Administrator 权限',
      'suspended.platform_admin_description': '身份有效，但此控制台仅供获批准的平台运营人员使用。',
      'field.email': '邮箱', 'field.work_email': '工作邮箱', 'field.password': '密码', 'field.new_password': '新密码',
      'field.organization_name': '组织名称', 'field.account_name': '账户名称', 'field.your_name': '你的姓名',
      'action.login': '登录', 'action.signup': '注册', 'action.open_invitation': '打开邀请', 'action.send_magic_link': '发送魔法链接',
      'action.send_reset_link': '发送重置链接', 'action.update_password': '更新密码', 'action.accept_invitation': '接受邀请',
      'action.logout': '退出登录', 'action.back_to_login': '返回登录', 'action.forgot_password': '忘记密码？',
      'action.create_account': '创建账户', 'action.remember_me': '记住我', 'action.or': '或',
      'copy.no_account': '还没有账户？', 'copy.already_registered': '已经注册？',
      'copy.password_hint': '请使用至少 8 个字符，包含大小写字母和数字。', 'copy.consent_prefix': '我同意',
      'copy.consent_and': '和', 'copy.terms': '服务条款', 'copy.privacy': '隐私声明', 'copy.role': '角色：{{role}}',
      'copy.status': '状态：{{status}}', 'copy.different_application': '此邀请属于另一个应用。',
      'copy.invited_identity': '使用受邀邮箱创建身份', 'copy.reference': '参考编号',
      'copy.support': '如有疑问，请联系支持团队或组织管理员。', 'footer.secure': '安全设计 · 隐私保护', 'aria.brand': '{{brand}} 品牌',
    },
  },
  billing: {
    'en-US': coreEnglishMessages,
    'zh-CN': {
      'common.continue': '继续',
      'billing.nav_label': '计费导航', 'billing.nav_pricing': '价格', 'billing.nav_billing': '账单',
      'billing.pricing_kicker': '简单、透明的计费',
      'billing.pricing_title': '选择适合你下一阶段的套餐。',
      'billing.pricing_body': '套餐和附加项仅适用于 {{brand}}。Paddle 负责税费与安全付款。',
      'billing.loading_plans_title': '正在加载套餐', 'billing.loading_plans_body': '正在获取实时产品目录…',
      'billing.no_plans_title': '暂未提供公开套餐', 'billing.no_plans_body': '此应用暂未配置可用的 Paddle 产品目录。',
      'billing.plans_label': '{{brand}} 套餐', 'billing.product_subscription': '订阅',
      'billing.product_usage_pack': '积分包', 'billing.product_addon': '附加项', 'billing.product_base_plan': '套餐',
      'billing.feature_monthly_image_credits': '每月 {{count}} 个图片积分',
      'billing.feature_monthly_visual_credits': '每月 {{count}} 个视觉积分',
      'billing.feature_monthly_assistant_messages': '每月 {{count}} 次 AI 回复',
      'billing.feature_one_brand': '1 个品牌档案', 'billing.feature_max_brands': '最多 {{count}} 个品牌档案',
      'billing.feature_max_products': '最多 {{count}} 个商品',
      'billing.feature_one_member': '1 名团队成员',
      'billing.feature_max_members': '最多 {{count}} 名团队成员',
      'billing.feature_one_website': '1 个网站',
      'billing.feature_max_websites': '最多 {{count}} 个网站',
      'billing.feature_one_agent': '1 名 Agent 成员', 'billing.feature_max_agents': '最多 {{count}} 名 Agent 成员',
      'billing.feature_max_knowledge_documents': '最多 {{count}} 个知识文档',
      'billing.feature_max_knowledge_storage': '{{count}} MB 知识库存储',
      'billing.feature_conversation_retention': '对话保留 {{count}} 天',
      'billing.feature_max_resolution': '最高 {{resolution}} 分辨率',
      'billing.feature_hd_download': '高清下载', 'billing.feature_priority_generation': '优先生成',
      'billing.feature_commercial_use': '商业使用权', 'billing.feature_no_watermark': '无水印下载',
      'billing.feature_sharing': '可分享的视觉页面', 'billing.feature_batch_import': 'CSV 和 XLSX 批量导入',
      'billing.feature_custom_branding': '自定义品牌', 'billing.feature_human_handover': '人工接管',
      'billing.feature_lead_export': '线索导出', 'billing.feature_webhooks': 'Assistant Webhook',
      'billing.feature_ai_sales_assistant': '商品感知 AI 助手和线索收集',
      'billing.feature_custom_widget': '自定义 Widget 样式', 'billing.feature_visual_generation_from_chat': '聊天触发视觉生成',
      'billing.feature_advanced_analytics': '高级 Assistant 分析', 'billing.feature_advanced_routing': '高级对话路由',
      'billing.feature_priority_models': '优先模型路由', 'billing.feature_api_access': 'API 接入就绪',
      'billing.feature_assistant_addon_eligible': '可购买 AI Visual Sales Assistant Add-on',
      'billing.preparing': '正在准备…', 'billing.start_trial': '开始 {{days}} 天试用',
      'billing.month': '/ 月', 'billing.year': '/ 年', 'billing.one_time': '一次性购买',
    },
    'es-ES': {
      'common.continue': 'Continuar',
      'billing.nav_label': 'Navegación de facturación', 'billing.nav_pricing': 'Precios', 'billing.nav_billing': 'Facturación',
      'billing.pricing_kicker': 'Facturación simple y transparente',
      'billing.pricing_title': 'Elige el plan que encaja con tu siguiente etapa.',
      'billing.pricing_body': 'Los planes y complementos están aislados para {{brand}}. Paddle gestiona los impuestos y el pago seguro.',
      'billing.loading_plans_title': 'Cargando planes', 'billing.loading_plans_body': 'Obteniendo el catálogo de productos en directo…',
      'billing.no_plans_title': 'Aún no hay planes públicos', 'billing.no_plans_body': 'Esta aplicación no tiene un catálogo activo de Paddle.',
      'billing.plans_label': 'Planes de {{brand}}', 'billing.product_subscription': 'suscripción',
      'billing.product_usage_pack': 'paquete de créditos', 'billing.product_addon': 'complemento', 'billing.product_base_plan': 'plan',
      'billing.feature_monthly_image_credits': '{{count}} créditos de imagen al mes',
      'billing.feature_monthly_visual_credits': '{{count}} créditos visuales al mes',
      'billing.feature_monthly_assistant_messages': '{{count}} respuestas de IA al mes',
      'billing.feature_one_brand': '1 perfil de marca', 'billing.feature_max_brands': 'Hasta {{count}} perfiles de marca',
      'billing.feature_max_products': 'Hasta {{count}} productos',
      'billing.feature_one_member': '1 miembro del equipo',
      'billing.feature_max_members': 'Hasta {{count}} miembros del equipo',
      'billing.feature_one_website': '1 sitio web',
      'billing.feature_max_websites': 'Hasta {{count}} sitios web',
      'billing.feature_one_agent': '1 miembro agente', 'billing.feature_max_agents': 'Hasta {{count}} miembros agentes',
      'billing.feature_max_knowledge_documents': 'Hasta {{count}} documentos de conocimiento',
      'billing.feature_max_knowledge_storage': '{{count}} MB de almacenamiento de conocimiento',
      'billing.feature_conversation_retention': '{{count}} días de retención de conversaciones',
      'billing.feature_max_resolution': 'Resolución máxima {{resolution}}',
      'billing.feature_hd_download': 'Descargas HD', 'billing.feature_priority_generation': 'Generación prioritaria',
      'billing.feature_commercial_use': 'Derechos de uso comercial', 'billing.feature_no_watermark': 'Descargas sin marca de agua',
      'billing.feature_sharing': 'Páginas visuales compartibles', 'billing.feature_batch_import': 'Importación masiva CSV y XLSX',
      'billing.feature_custom_branding': 'Marca personalizada', 'billing.feature_human_handover': 'Transferencia humana',
      'billing.feature_lead_export': 'Exportación de leads', 'billing.feature_webhooks': 'Webhooks del asistente',
      'billing.feature_ai_sales_assistant': 'Asistente de IA con catálogo y captura de leads',
      'billing.feature_custom_widget': 'Estilo de Widget personalizado', 'billing.feature_visual_generation_from_chat': 'Generación visual desde el chat',
      'billing.feature_advanced_analytics': 'Analítica avanzada del asistente', 'billing.feature_advanced_routing': 'Enrutamiento avanzado de conversaciones',
      'billing.feature_priority_models': 'Enrutamiento prioritario de modelos', 'billing.feature_api_access': 'Acceso API preparado',
      'billing.feature_assistant_addon_eligible': 'Elegible para AI Visual Sales Assistant Add-on',
      'billing.preparing': 'Preparando…', 'billing.start_trial': 'Iniciar prueba de {{days}} días',
      'billing.month': '/ mes', 'billing.year': '/ año', 'billing.one_time': 'pago único',
    },
  },
  email: { 'en-US': coreEnglishMessages },
  widget: { 'en-US': coreEnglishMessages },
  ai: { 'en-US': coreEnglishMessages },
  rag: { 'en-US': coreEnglishMessages },
  state: {
    'en-US': {
      'state.route_error_title': 'Something did not load correctly',
      'state.route_error_body': 'Your information is safe. Try again, and contact support if the problem continues.',
      'state.retry': 'Try again',
      'state.contact_support': 'Contact support',
      'state.loading_title': 'Loading your information', 'state.loading_body': 'This can take a moment.',
      'state.empty_title': 'Nothing here yet', 'state.empty_body': 'Create or add something to get started.',
      'state.partial_title': 'Some information is unavailable', 'state.partial_body': 'The available information is shown. Try again shortly for the rest.',
      'state.network_title': 'Check your connection', 'state.network_body': 'We could not reach the service. Check your connection and try again.',
      'state.unavailable_title': 'Service temporarily unavailable', 'state.unavailable_body': 'Please try again in a moment. Your information has not been changed.',
      'state.provider_failure_title': 'AI service is temporarily unavailable', 'state.provider_failure_body': 'No credit was spent for this failed attempt. Please try again shortly.',
      'state.queue_delay_title': 'Still processing', 'state.queue_delay_body': 'Your request is safe in the queue. You can return while we finish it.',
      'state.credits_exhausted_title': 'More credits are needed', 'state.credits_exhausted_body': 'Add credits or choose a plan before continuing.',
      'state.subscription_required_title': 'A subscription is required', 'state.subscription_required_body': 'Choose a plan to use this feature.',
      'state.addon_required_title': 'An add-on is required', 'state.addon_required_body': 'Add this feature to your plan before continuing.',
      'state.permission_denied_title': 'You do not have access', 'state.permission_denied_body': 'Ask an account owner or administrator to review your access.',
      'state.unknown_error_title': 'We could not complete that request', 'state.unknown_error_body': 'Try again. Your information has not been changed.',
      'cookie.preferences_aria': 'cookie preferences', 'cookie.title': 'Privacy choices',
      'cookie.description_before_privacy_link': '{{appName}} uses necessary storage to keep the service secure and working. Optional analytics helps us improve the product and is off until you choose it. Read our ',
      'cookie.privacy_notice': 'Privacy Notice', 'cookie.description_after_privacy_link': '.',
      'cookie.optional_analytics_label': 'Optional analytics', 'cookie.optional_analytics_description': 'Measure aggregate product usage. It is not required to use {{appName}}.',
      'cookie.save_choices': 'Save choices', 'cookie.accept_optional_analytics': 'Accept optional analytics',
      'cookie.use_necessary_only': 'Use necessary only', 'cookie.manage_choices': 'Manage choices', 'cookie.back': 'Back',
    },
    'zh-CN': {
      'state.route_error_title': '页面未能正确加载',
      'state.route_error_body': '你的信息仍然安全。请重试；如果问题持续存在，请联系支持团队。',
      'state.retry': '重试',
      'state.contact_support': '联系支持团队',
      'state.loading_title': '正在加载你的信息', 'state.loading_body': '这可能需要片刻。',
      'state.empty_title': '这里暂时没有内容', 'state.empty_body': '创建或添加内容即可开始。',
      'state.partial_title': '部分信息暂不可用', 'state.partial_body': '当前展示可用信息，稍后重试可获取其余内容。',
      'state.network_title': '请检查网络连接', 'state.network_body': '暂时无法连接服务，请检查网络后重试。',
      'state.unavailable_title': '服务暂时不可用', 'state.unavailable_body': '请稍后重试。你的信息未被修改。',
      'state.provider_failure_title': 'AI 服务暂时不可用', 'state.provider_failure_body': '本次失败不会扣除积分，请稍后重试。',
      'state.queue_delay_title': '仍在处理中', 'state.queue_delay_body': '你的请求已安全进入队列，可以稍后回来查看结果。',
      'state.credits_exhausted_title': '需要更多积分', 'state.credits_exhausted_body': '请添加积分或选择套餐后继续。',
      'state.subscription_required_title': '需要订阅', 'state.subscription_required_body': '选择套餐后即可使用此功能。',
      'state.addon_required_title': '需要附加项', 'state.addon_required_body': '将此功能添加到套餐后再继续。',
      'state.permission_denied_title': '你没有访问权限', 'state.permission_denied_body': '请联系账户所有者或管理员检查你的权限。',
      'state.unknown_error_title': '未能完成该请求', 'state.unknown_error_body': '请重试。你的信息未被修改。',
      'cookie.preferences_aria': 'Cookie 偏好', 'cookie.title': '隐私选择',
      'cookie.description_before_privacy_link': '{{appName}} 使用必要存储来保障服务安全和正常运行。可选分析功能可帮助我们改进产品，只有在你选择后才会启用。请阅读我们的',
      'cookie.privacy_notice': '隐私声明', 'cookie.description_after_privacy_link': '。',
      'cookie.optional_analytics_label': '可选分析', 'cookie.optional_analytics_description': '用于衡量产品的汇总使用情况。使用 {{appName}} 不需要开启此功能。',
      'cookie.save_choices': '保存选择', 'cookie.accept_optional_analytics': '接受可选分析',
      'cookie.use_necessary_only': '仅使用必要存储', 'cookie.manage_choices': '管理选择', 'cookie.back': '返回',
    },
    'es-ES': {
      'state.route_error_title': 'No se pudo cargar correctamente',
      'state.route_error_body': 'Tu información está segura. Inténtalo de nuevo y contacta con soporte si el problema continúa.',
      'state.retry': 'Intentar de nuevo',
      'state.contact_support': 'Contactar con soporte',
      'state.loading_title': 'Cargando tu información', 'state.loading_body': 'Puede tardar un momento.',
      'state.empty_title': 'Aún no hay nada aquí', 'state.empty_body': 'Crea o añade algo para empezar.',
      'state.partial_title': 'Parte de la información no está disponible', 'state.partial_body': 'Se muestra la información disponible. Vuelve a intentarlo pronto para ver el resto.',
      'state.network_title': 'Comprueba tu conexión', 'state.network_body': 'No pudimos contactar con el servicio. Comprueba tu conexión e inténtalo de nuevo.',
      'state.unavailable_title': 'Servicio temporalmente no disponible', 'state.unavailable_body': 'Vuelve a intentarlo en un momento. Tu información no ha cambiado.',
      'state.provider_failure_title': 'El servicio de IA no está disponible temporalmente', 'state.provider_failure_body': 'No se gastó ningún crédito en este intento fallido. Vuelve a intentarlo pronto.',
      'state.queue_delay_title': 'Aún se está procesando', 'state.queue_delay_body': 'Tu solicitud está segura en la cola. Puedes volver mientras terminamos.',
      'state.credits_exhausted_title': 'Se necesitan más créditos', 'state.credits_exhausted_body': 'Añade créditos o elige un plan antes de continuar.',
      'state.subscription_required_title': 'Se requiere una suscripción', 'state.subscription_required_body': 'Elige un plan para usar esta función.',
      'state.addon_required_title': 'Se requiere un complemento', 'state.addon_required_body': 'Añade esta función a tu plan antes de continuar.',
      'state.permission_denied_title': 'No tienes acceso', 'state.permission_denied_body': 'Pide al propietario o administrador de la cuenta que revise tu acceso.',
      'state.unknown_error_title': 'No pudimos completar la solicitud', 'state.unknown_error_body': 'Vuelve a intentarlo. Tu información no ha cambiado.',
      'cookie.preferences_aria': 'preferencias de cookies', 'cookie.title': 'Opciones de privacidad',
      'cookie.description_before_privacy_link': '{{appName}} utiliza el almacenamiento necesario para mantener el servicio seguro y operativo. Las analíticas opcionales nos ayudan a mejorar el producto y permanecen desactivadas hasta que las elijas. Lee nuestro ',
      'cookie.privacy_notice': 'Aviso de privacidad', 'cookie.description_after_privacy_link': '.',
      'cookie.optional_analytics_label': 'Analíticas opcionales', 'cookie.optional_analytics_description': 'Miden el uso agregado del producto. No son necesarias para usar {{appName}}.',
      'cookie.save_choices': 'Guardar opciones', 'cookie.accept_optional_analytics': 'Aceptar analíticas opcionales',
      'cookie.use_necessary_only': 'Usar solo las necesarias', 'cookie.manage_choices': 'Gestionar opciones', 'cookie.back': 'Volver',
    },
  },
};

export function createAppTranslator(
  namespace: AppI18nNamespace,
  locale?: string | null,
  onMissing?: (event: TranslationMissingEvent) => void,
) {
  const catalog = appMessageCatalogs[namespace];
  const fallbackCatalog = catalog[DEFAULT_LOCALE];
  return createTranslator({
    namespace,
    ...(locale !== undefined ? { locale } : {}),
    catalog,
    ...(fallbackCatalog ? { fallbackCatalog } : {}),
    ...(onMissing ? { onMissing } : {}),
  });
}

export function findNamespaceMissingTranslations(
  namespace: AppI18nNamespace,
  locales: readonly SupportedLocale[] = supportedLocales,
): TranslationMissingEvent[] {
  const catalog = appMessageCatalogs[namespace];
  const requiredKeys = Object.keys(catalog[DEFAULT_LOCALE] ?? {});
  return locales.flatMap((locale) => requiredKeys
    .filter((key) => catalog[locale]?.[key] === undefined)
    .map((key) => ({ namespace, locale, key })));
}
